<template>
  <div>
    <div
      class="strong"
      title="Items that are used in the CONCERN prediction algorithm"
    >
      Factors
    </div>
    
    <div class="flex mt-1">
      <div class="flex container width-10">
        <ul>
          <li v-for="( factor, i ) in whatIfValues"
              :key="factor.Element"
              class="factor-tab px-1 py-1 bg-light-gray b bb bl br bc-medium-gray"
              :class="{ active: ( i === activeFactorIndex ), bt: ( i === 0 ) }"
              @click="activeFactorIndex = i"
              :title="factorExplanation( factor.Element )"
          >
            <div>{{ displayName( factor.Element ) }}</div>
            <div
              class="pill mt-1_2 height-1_2"
              :style="`width: ${factor.value * 10}%;`"
            >
            </div>
          </li>
        </ul>
      </div>

    <div class="flex-grow container bg-almost-white px-1 py-1 b bt bb br bc-medium-gray">

      <ul style="max-width:500px;">
        <li v-if="noActiveContent">No content available at this time</li>
        <li
          v-for="( item, i ) in activeContent"
          :key="item.Created_at + i"
          class="py-1_2"
          style="border-left: 1px dotted gray;"
        >
          <div class="timeline-circle" />
          <div class="fs-sm timeline-date">{{item.displayDate}}</div>
          <div class="timeline-entry mt-1_2">
            
            <span
              class="strong"
              v-if="item.Element !== 'notes'"
            >
              {{item.displayName}}: 
            </span>
            
            <span
              v-if="item.Element !== 'notes'"
            >
              {{item.value}} {{item.uom}} {{item.comment}}
            </span>
                        
            <span class="strong"
              v-if="item.Element === 'notes'"
            >
              {{item.name}} ({{item.author}})
            </span>
            
            <span
              v-if="item.Element === 'notes'"
              v-html="item.content"
            />

          </div>
        </li>
      </ul>

    </div>
    </div>
  </div>
</template>

<script>
import { doLogging }  from '../modules/log';
import { mapGetters } from 'vuex';

export default {

  name: 'ConcernFactors',

  props: {
    factors: { type: Array, required: true },
  },

  data () {
    return {
      activeFactorIndex: 0,
    };
  },

  computed: {
    ...mapGetters([
      'currentDtm',
      'documents',
      'vitals',
      'medications',
      'loggingContext',
      'concernIsZero',
    ]),

    //  limit lookback period for factors to display
    factorStartDtmISO () {
      const curr = new Date( this.currentDtm );
      curr.setDate( curr.getDate() - 1 );
      return curr && curr.toISOString() || '';
    },

    whatIfValues () {

      const latestFactors = this.factors && this.factors[0] || null;

      if ( !latestFactors) { return [] }


      const whatIfTypes = [
        'wi_comments',
        'wi_vitals',
        'wi_mar',
        'wi_notes',
        'wi_note_content',
      ];

      const latestValues = whatIfTypes.map( t => ({
        Element: t,
        value: latestFactors.value[t]
      }));
 
      return latestValues;
      
      // return latestValues.sort( ( a, b ) => {
      //   return ( parseInt( a.value ) < parseInt( b.value ) ) ? 1 : -1;
      // });
    },

    activeFactorName () {
      const activeFactor = this.whatIfValues[ this.activeFactorIndex ];
      return activeFactor && activeFactor.Element &&
             activeFactor.Element.replace( 'wi_', '' ) || '';
    },

    activeContent () {

      const activeFactor = this.whatIfValues[ this.activeFactorIndex ];

      const elementName = activeFactor
                        && activeFactor.Element
                        && activeFactor.Element
                            .replace( 'wi_', 'factor_' )
                            .replace( 'factor_notes', 'notes' )
                            .replace( 'factor_note_content', 'notes' )
                            // .replace( 'note_meta', 'note_content' )
                        || null;

      if ( !elementName ) {
        return [];
      }

      if ( this.concernIsZero ) {
        return [];
      }

      if ( elementName === 'notes' ) {
        return this.documents.map( d => ({
          ...d,
          displayDate: this.displayDate( d.dtm ),
          // displayDate: d.dtm.substring( 5, 10 ) + ' '
          //            + d.dtm.substring( 11, 16 ),
          Element: 'notes'
        }));
      }

      if ( elementName === 'factor_vitals' ) {

        return this.vitals.map( d => ({
          ...d,
          displayDate: this.displayDate( d.dtm ),
          // displayDate: d.dtm.substring( 5, 10 ) + ' '
          //            + d.dtm.substring( 11, 16 ),
          Element: 'vitals'
        }));        
      }

      if ( elementName === 'factor_mar' ) {

        return this.medications.map( d => ({
          ...d,
          displayName: d.medication_display,
          value: ( d.is_prn === 'true' ) ? 'prn ' + d.mapped_action : d.mapped_action,
          displayDate: d.administration_dtm && d.administration_dtm.substring( 5, 16 ) || '',
          Element: 'mar'
        }));        
      }


      if ( elementName === 'factor_comments' ) {

        return this.vitals.map( d => ({
          ...d,
          displayDate: this.displayDate( d.dtm ),
          // displayDate: d.dtm.substring( 5, 10 ) + ' '
          //            + d.dtm.substring( 11, 16 ),
          Element: 'vitals'
        }))
        .filter( d => d.comment !== '' );
      }

      return [];
      
    },

    noActiveContent () {
      return ( this.activeContent.length === 0 );
    },
  },

  methods: {

    displayDate ( isoString ) {

      const d = new Date( isoString );
      const m = d.getMonth() + 1;
      const mm = ( m < 10 ) ? '0' + m : '' + m;
      const day = d.getDate();
      const dd = ( day < 10 ) ? '0' + day : '' + day;
      const h = d.getHours();
      const hh = ( h < 10 ) ? '0' + h : '' + h;
      const mi = d.getMinutes();
      const mimi = ( mi < 10 ) ? '0' + mi : '' + mi;

      return `${ mm }-${ dd } ${ hh }:${ mimi }`;
    },

    displayName ( elementName ) {
      return {
        'wi_vitals': 'Vital Sign Frequency',
        'wi_comments': 'VS Comment Frequency',
        'wi_mar': 'Medication Administration',
        'wi_notes': 'Nursing Note Frequency',
        'wi_note_content': 'Nursing Note Content',
      }[ elementName ] || '';
    },

    factorExplanation ( elementName ) {
      return {
        'wi_vitals': 'Analysis of the pattern of the vital sign frequency for this patient',
        'wi_comments': 'Analysis of the pattern of vital sign comments',
        'wi_mar': 'Analysis of medication administration patterns',
        'wi_notes': 'Analysis of the pattern of note writing for this patient',
        'wi_note_content': 'Analysis of nursing note content for this patient',
      }[ elementName ] || '';
    },

    log ( event ) {
      event = { ...event, ...this.loggingContext };
      doLogging( event );
    },
  },

  watch: {

    activeFactorName: function () {
      this.log( {eventType: 'factor displayed', eventDetails: this.activeFactorName } );
    }
  }
 
};
</script>

<style lang="scss">
li.factor-tab {
  cursor: pointer;
  line-height: 1.3em;
  width: 160px;
}

li.factor-tab.active {
  z-index: 10;
  margin-right: -1px;
  background-color: $almost-white;
  border-right-color: $almost-white;
}

.pill {
  background-color: $medium-gray;
  border-radius: $gutter/2;
}

.timeline-circle {
  display: inline-block;
  height: 10px;
  width: 10px;
  border-radius: 5px;
  border: 2px solid $medium-gray;
  margin-left: -5px;
  background-color: $almost-white;
}

.timeline-date {
  padding-left: 5px;
  display: inline-block;
}

.timeline-entry {
  padding-left: 10px;
}
</style>
